import { ExternalLinkIcon, Skeleton } from '@commander/ui';
import { useCommanderGoAnalytics } from '../../hooks/useCommanderGoAnalytics.js';
import type { TimelineBlock, TimelineEvent } from '../blocks/timeline.js';
import { ExpandableBlock, ExpandTrigger } from './ExpandableBlock.js';
import { formatValue, sanitizeHref, trackDeepLinkClick } from './utils.js';

// --- Event Type Colors ---

const EVENT_TYPE_COLORS: Record<string, string> = {
  email: 'bg-blue-500',
  call: 'bg-green-500',
  meeting: 'bg-purple-500',
  stage_change: 'bg-orange-500',
  note: 'bg-gray-400',
  created: 'bg-teal-500',
};

function getEventDotColor(type?: string): string {
  if (!type) return 'bg-gray-400';
  return EVENT_TYPE_COLORS[type] ?? 'bg-gray-400';
}

// --- Sort Events ---

function sortEvents(
  events: TimelineEvent[],
  order: 'newest_first' | 'oldest_first'
): TimelineEvent[] {
  return [...events].sort((a, b) => {
    const ta = new Date(a.date).getTime();
    const tb = new Date(b.date).getTime();
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return order === 'newest_first' ? tb - ta : ta - tb;
  });
}

// --- Single Event ---

const INLINE_EVENT_LIMIT = 5;

function EventItem({
  event,
  isLast,
  onDeepLinkClick,
}: {
  event: TimelineEvent;
  isLast: boolean;
  onDeepLinkClick?: (href: string) => void;
}) {
  return (
    <div className="relative flex gap-3 pb-4">
      {/* Timeline line */}
      {!isLast && <div className="absolute left-[5px] top-3 bottom-0 w-px bg-border" />}

      {/* Dot */}
      <div
        className={`relative z-10 mt-1.5 h-[11px] w-[11px] flex-shrink-0 rounded-full ${getEventDotColor(event.type)}`}
      />

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <time
            dateTime={event.date}
            className="text-[10px] text-muted-foreground whitespace-nowrap"
          >
            {formatValue(event.date, 'date')}
          </time>
          {event.type && (
            <span className="text-[10px] capitalize text-muted-foreground">
              {event.type.replace('_', ' ')}
            </span>
          )}
        </div>
        <div className="mt-0.5">
          {(() => {
            const safeHref = sanitizeHref(event.deep_link);
            return safeHref ? (
              <a
                href={safeHref}
                onClick={() => onDeepLinkClick?.(safeHref)}
                className="text-xs font-medium text-primary-700 hover:underline dark:text-primary-300"
              >
                {event.title}
              </a>
            ) : (
              <p className="text-xs font-medium">{event.title}</p>
            );
          })()}
        </div>
        {event.description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{event.description}</p>
        )}
        {event.metadata && Object.keys(event.metadata).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            {Object.entries(event.metadata).map(([key, val]) => (
              <span key={key} className="text-[10px] text-muted-foreground">
                <span className="font-medium">{key}:</span> {val}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Timeline Content ---

function TimelineContent({
  data,
  limit,
  onDeepLinkClick,
}: {
  data: TimelineBlock;
  limit?: number;
  onDeepLinkClick?: (href: string) => void;
}) {
  const order = data.order ?? 'newest_first';
  const sorted = sortEvents(data.events, order);
  const events = limit ? sorted.slice(0, limit) : sorted;
  const remaining = limit ? sorted.length - events.length : 0;

  if (events.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-xs text-muted-foreground">
        No events to display
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-xs font-semibold">{data.title}</h4>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground">
              {data.events.length} event{data.events.length !== 1 ? 's' : ''}
            </span>
            <ExpandTrigger />
          </div>
        </div>
        {data.entity && (
          <div className="mt-0.5">
            {(() => {
              const safeHref = sanitizeHref(data.entity_deep_link);
              return safeHref ? (
                <a
                  href={safeHref}
                  onClick={() => onDeepLinkClick?.(safeHref)}
                  className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline dark:text-primary-300"
                >
                  {data.entity}
                  <ExternalLinkIcon className="h-3 w-3" />
                </a>
              ) : (
                <span className="text-xs text-muted-foreground">{data.entity}</span>
              );
            })()}
          </div>
        )}
      </div>

      {/* Events */}
      <div className="px-3 pb-2">
        {events.map((event, i) => (
          <EventItem
            key={`${event.date}-${event.title}-${i}`}
            event={event}
            isLast={i === events.length - 1}
            onDeepLinkClick={onDeepLinkClick}
          />
        ))}
      </div>

      {/* Truncation footer */}
      {remaining > 0 && (
        <div className="border-t px-3 py-1.5">
          <p className="text-[10px] text-muted-foreground">
            {remaining} more event{remaining !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}

// --- Main Timeline Component ---

export function Timeline({ data }: { data: TimelineBlock }) {
  const analyticsCtx = useCommanderGoAnalytics();
  const handleDeepLinkClick = (href: string) => {
    trackDeepLinkClick(analyticsCtx, 'timeline', href);
  };

  return (
    <ExpandableBlock
      title={data.title}
      expandedContent={<TimelineContent data={data} onDeepLinkClick={handleDeepLinkClick} />}
    >
      <div className="rounded-lg border bg-card text-card-foreground">
        <TimelineContent
          data={data}
          limit={INLINE_EVENT_LIMIT}
          onDeepLinkClick={handleDeepLinkClick}
        />
      </div>
    </ExpandableBlock>
  );
}

// --- Skeleton ---

export function TimelineSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-3">
      <Skeleton className="h-3 w-[50%]" />
      <div className="mt-3 space-y-4">
        {['a', 'b', 'c'].map((id) => (
          <div key={id} className="flex gap-3">
            <Skeleton className="h-3 w-3 flex-shrink-0 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-2.5 w-16" />
              <Skeleton className="mt-1 h-3 w-[70%]" />
              <Skeleton className="mt-1 h-2.5 w-[50%]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
